import {
  brandTokens,
  isValidHex,
  toneTokens,
  type NeutralTone,
  type ThemeMode,
} from '@aurora/ui-kit';
import { useSyncExternalStore } from 'react';

import { api } from '../api/client.js';

/**
 * Mandanten-Branding (v6/E1): Name, Zeichen, Logo, Markenfarbe, Grundton.
 * Quelle ist GET /api/v1/branding (öffentlich — Login-/Portal-Seiten zeigen
 * die Marke VOR der Anmeldung). Der letzte Stand wird in localStorage
 * gecacht, damit der erste Paint nach einem Reload nicht in Aurora-Gold
 * aufblitzt, bevor die Antwort da ist.
 *
 * Bewusst KEIN Import von theme.ts (Zirkularität): die Anwendung der
 * CSS-Variablen bekommt den Modus als Parameter; verdrahtet wird in main.tsx.
 */
export interface Branding {
  brandName: string | null;
  brandShort: string | null;
  brandMark: string | null;
  logoPath: string | null;
  accentHex: string | null;
  neutralTone: NeutralTone;
}

const STORAGE_KEY = 'aurora.branding';
const listeners = new Set<() => void>();

function readCache(): Branding | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Branding;
    // Minimal-Validierung: kaputter Cache darf das Theming nicht crashen.
    if (parsed.accentHex !== null && !isValidHex(parsed.accentHex)) return null;
    return parsed;
  } catch {
    return null;
  }
}

let current: Branding | null = readCache();

export const brandingStore = {
  get(this: void): Branding | null {
    return current;
  },
  set(this: void, branding: Branding | null): void {
    current = branding;
    try {
      if (branding) localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage voll/gesperrt — Branding wirkt trotzdem für diese Sitzung.
    }
    document.title = branding?.brandName ?? 'Aurora ERP';
    for (const listener of listeners) listener();
  },
  subscribe(this: void, listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useBranding(): Branding | null {
  return useSyncExternalStore(brandingStore.subscribe, brandingStore.get);
}

/**
 * Branding vom Server holen. `tenantSlug` (z. B. aus dem Portal-Magic-Link)
 * gewinnt über den x-tenant-Header — deshalb wird der Header dann unterdrückt.
 */
export async function refreshBranding(tenantSlug?: string): Promise<void> {
  try {
    const query = tenantSlug !== undefined ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    const { branding } = await api<{ branding: Branding }>(`/api/v1/branding${query}`, {
      ...(tenantSlug !== undefined ? { tenant: false } : {}),
    });
    brandingStore.set(branding);
  } catch {
    // Öffentlicher Endpunkt nicht erreichbar → letzter Cache-Stand bleibt.
  }
}

/**
 * URL des Mandanten-Logos (öffentlicher Endpunkt). `v=logoPath` als
 * Cache-Buster: jeder Upload erzeugt einen neuen Pfad, alte Browser-Caches
 * können nie ein veraltetes Logo festhalten.
 */
export function brandingLogoUrl(
  branding: Branding | null,
  tenantSlug: string | null,
): string | null {
  if (branding?.logoPath == null || tenantSlug === null) return null;
  return `/api/v1/branding/logo?tenant=${encodeURIComponent(tenantSlug)}&v=${encodeURIComponent(branding.logoPath)}`;
}

/** Zuletzt gesetzte Inline-Variablen — werden vor jeder Anwendung geräumt. */
let appliedVars: string[] = [];

const TONE_VAR_NAMES = [
  'bg',
  'surface',
  'surfaceHover',
  'surfaceActive',
  'border',
  'borderStrong',
  'text',
  'textMuted',
  'textFaint',
] as const;

const TONE_CSS_NAMES: Record<(typeof TONE_VAR_NAMES)[number], string> = {
  bg: '--au-bg',
  surface: '--au-surface',
  surfaceHover: '--au-surface-hover',
  surfaceActive: '--au-surface-active',
  border: '--au-border',
  borderStrong: '--au-border-strong',
  text: '--au-text',
  textMuted: '--au-text-muted',
  textFaint: '--au-text-faint',
};

/**
 * Wendet das Branding als Inline-Variablen auf <html> an — Inline schlägt
 * jede Regel aus tokens.css/accents.css. Wird von applyTheme() bei jedem
 * Theme-/Akzent-/Branding-Wechsel aufgerufen.
 *
 * - `useBrandAccent` (Nutzerwahl „Marke"): volle abgeleitete Skala.
 * - sonst: nur der Grundton (der ist Instanz-Identität, keine
 *   Personalisierung) — Akzent kommt weiter aus den data-accent-Presets.
 */
export function applyBrandingVars(mode: ThemeMode, useBrandAccent: boolean): void {
  const style = document.documentElement.style;
  for (const name of appliedVars) style.removeProperty(name);
  appliedVars = [];

  const branding = current;
  if (!branding) return;

  let vars: Record<string, string> = {};
  if (useBrandAccent && branding.accentHex !== null && isValidHex(branding.accentHex)) {
    vars = brandTokens(branding.accentHex, branding.neutralTone)[mode];
  } else if (branding.neutralTone !== 'warm') {
    const tones = toneTokens(branding.neutralTone, mode);
    for (const key of TONE_VAR_NAMES) {
      vars[TONE_CSS_NAMES[key]] = tones[key];
    }
    if (mode === 'light') {
      // Light-primary ist der Ink des Grundtons; Dark-primary bleibt beim
      // Preset-Akzent Sache von tokens.css (heute Gold).
      vars['--au-primary'] = tones.primary;
      vars['--au-primary-hover'] = tones.primaryHover;
      vars['--au-primary-contrast'] = tones.primaryContrast;
    }
  }

  for (const [name, value] of Object.entries(vars)) {
    style.setProperty(name, value);
    appliedVars.push(name);
  }
}
